import React, { useEffect, useState } from "react";

import {
	Grid,
	Box,
	Select,
	MenuItem,
	IconButton,
	useMediaQuery,
	FormControl,
	Typography,
	ThemeProvider,
	Divider,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AmountTextField from "./AmountTextField";
import { SHADOW_FLAG_SIZE } from "../styles/Sizes";
import { SUPPORT_CCY_LIST } from "../utils/CurrencyHelper";
import { BLUE_5EA0, GREY_3141, GREY_5050, GREY_6263 } from "../styles/Colors";
import CurrencyConfig from "../json/CurrencyConfig.json";
import { Objects } from "../models/CurrencyPair";
import ShadowFlag from "../utils/CountryFlagShadow"

interface SelectionValues {
	defaultValue: string;
	selectedValue: string;
	side: string;
	onChangeCcy: any;
	displayAmount: string;
	onChangeValue: any;
	focusedSide: any;
}

const boxStyle = {
	height: '76px',
	borderRadius: '8px',
	direction: "column",
	backgroundColor: "white",
	alignItems: "center",
	display: "flex",
	justifyContent: "space-between",
};

function CurrencySelectionView(props: SelectionValues) {
	const {
		defaultValue,
		selectedValue,
		side,
		onChangeCcy,
		displayAmount,
		onChangeValue,
		focusedSide,
	} = props;

	const [open, setOpen] = React.useState(false);

	const handleClose = () => {
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const [checked, setChecked] = useState(false);
	const isNarrow = useMediaQuery("(max-width:599px)");

	useEffect(() => {
		setChecked(isNarrow);
	}, [isNarrow]);

	const theme = useTheme();

	theme.typography.h2 = {
		fontSize: "16px",
		fontFamily: "Avenir",
		fontWeight: '900',
		[theme.breakpoints.down("sm")]: {
			fontSize: "15px",
		},
		[theme.breakpoints.down("xs")]: {
			fontSize: "15px",
		},
	};

	theme.typography.h1 = {
		fontSize: "20px",
		fontFamily: "Avenir",
	};

	theme.typography.body1 = {
		fontSize: "20px",
		fontFamily: "Avenir",
		[theme.breakpoints.down("sm")]: {
			fontSize: "15px",
		},
		[theme.breakpoints.down("xs")]: {
			fontSize: "15px",
		},
	};

	theme.typography.body2 = {
		fontSize: "20px",
		fontFamily: "Avenir",
		[theme.breakpoints.down("sm")]: {
			fontSize: "13px",
		},
	}

	const currencyConfig: Objects = CurrencyConfig;

	const getCcyOptions = (): readonly string[] => {
		const currencies = SUPPORT_CCY_LIST.map((item) => {
			return `${item}`;
		});
		return currencies;
	};

	const renderCurrency = (item: string) => {
		if (item.length === 0) {
			return (
				<Box
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minWidth: 95,
					}}
				>
					<ThemeProvider theme={theme}>
						<Typography variant="body2" color={GREY_3141} sx={{ opacity: 0.2, fontWeight: 'bold' }} pt={0.4} noWrap>
							Select Currency
						</Typography>
					</ThemeProvider>
				</Box>
			);
		} else {
			return (
				<Box
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minWidth: 70,
					}}
					sx={{ "& > img": { mr: 1, flexShrink: 0 } }}
					{...props}
				>
					<img
						loading="lazy"
						width={SHADOW_FLAG_SIZE}
						src={ShadowFlag(item)}
						alt=""
						style={{paddingTop: '0.05rem'}}
					/>
					{checked ? (
						<ThemeProvider theme={theme}>
							<Typography variant="h2" color={GREY_5050} pt="0.2rem">{item}</Typography>
						</ThemeProvider>
					) : (
						<ThemeProvider theme={theme}>
							<Typography variant="h2" color={GREY_5050} pt="0.1rem" noWrap>
								{item} - {currencyConfig[item].name.en_name}
							</Typography>
						</ThemeProvider>
					)}
				</Box>
			);
		}
	};
	const renderStartAdornment = (item: string) => {
		return (
			<Box
				style={{
					display: "flex",
					justifyContent: "left",
					alignItems: "center",
				}}
				sx={{ "& > img": { mr: 1, flexShrink: 0 } }}
				{...props}
			>
				<img
					loading="lazy"
					width={SHADOW_FLAG_SIZE}
					src={ShadowFlag(item)}
					alt=""
				/>

				<ThemeProvider theme={theme}>
					<Typography
						variant="h2"
						sx={{ textOverflow: "ellipsis" }}
						noWrap={true}
						color={GREY_3141}
					>
						{item} - {currencyConfig[item].name.en_name}
					</Typography>
				</ThemeProvider>
			</Box>
		);
	};


	return (
		<div className="ccy-selection-view">
			<Grid container sx={boxStyle}>
				<Grid item xs={6} sm={5} md={5.5} lg={5}>
					<AmountTextField
						ccy={selectedValue}
						value={displayAmount}
						onChangeValue={onChangeValue}
						setFocusedSide={focusedSide}
						side={side}
					/>
				</Grid>
				<Divider
					sx={{ height: 45, m: 0.5, backgroundColor: GREY_6263, opacity: 1 }}
					orientation="vertical"
				/>
				<Grid item xs={5} sm sx={{ paddingLeft: 0.5, minWidth: 75 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between", //flex-end for google's design
							alignItems: "center",
						}}
					>
						<FormControl>
							<Select
								className="SelectComponent"
								IconComponent={() => null}
								open={open}
								onClose={handleClose}
								onOpen={handleOpen}
								disableUnderline
								variant="standard"
								color="secondary"
								defaultValue={defaultValue}
								displayEmpty
								autoWidth={true}
								onChange={onChangeCcy}
								renderValue={(value) => {
									return renderCurrency(value);
								}}
							>
								{getCcyOptions().map((option) => (
									<MenuItem key={option} value={option}>
										{renderStartAdornment(option)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					{open ? (
							<IconButton
								onClick={handleOpen}
								sx={{ "&:hover": { backgroundColor: "white" }, pr: "1.8rem" }}
							>
								<KeyboardArrowUpIcon
									sx={{ color: BLUE_5EA0, fontSize: 20 }}
								/>
							</IconButton>
						) : (
							<IconButton
								onClick={handleOpen}
								sx={{ "&:hover": { backgroundColor: "white" }, pr: "1.8rem" }}
							>
								<KeyboardArrowDownIcon
									sx={{ color: BLUE_5EA0, fontSize: 20 }}
								/>
							</IconButton>
					)}
					</Box>
				</Grid>
			</Grid>
		</div>
	);
}

export default CurrencySelectionView;
